import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { Button, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '~/components';
import { useRefreshStickers } from '~/hooks/use-refresh-stickers';

interface RefreshStickersSheetProps {
  packId: string;
  onSuccess?: () => void;
}

export function RefreshStickersSheet({ packId, onSuccess }: RefreshStickersSheetProps) {
  const queryClient = useQueryClient();
  const t = useTranslations('my-collection.nft.actions');
  const { recreateStickersPack, error, isPending, activeRefreshCount, isButtonDisabled } = useRefreshStickers({
    onSuccess: () => {
      void queryClient.invalidateQueries();
      onSuccess?.();
    },
  });

  const handleRefresh = () => {
    void recreateStickersPack({
      data: {
        packId,
      },
    });
  };

  return (
    <SheetContent className="rounded-t-3xl">
      <SheetHeader>
        <div className="flex flex-col gap-4 pt-4">
          <SheetTitle>
            <div className="text-center text-white font-bold text-[24px]">{t('refresh-stickers.title')}</div>
          </SheetTitle>
          <SheetDescription>
            <div className="font-bold text-white/60 text-[16px] text-center">{t('refresh-stickers.description')}</div>
          </SheetDescription>
        </div>
      </SheetHeader>

      <div className="flex flex-col gap-6 mt-6">
        {error ? (
          <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-4">
            <p className="text-sm text-red-200">
              {(() => {
                const httpError = error as { response?: { data?: { message?: string } } };
                const errorMessage = httpError.response?.data?.message;
                return errorMessage ?? t('refresh-stickers.error');
              })()}
            </p>
          </div>
        ) : null}

        <div className="pb-6">
          <Button className="w-full" disabled={isButtonDisabled} variant="rainbow" onClick={handleRefresh}>
            {(() => {
              if (isPending) {
                return t('refresh-stickers.refreshing');
              }
              if (activeRefreshCount > 0) {
                return t('refresh-stickers.refresh', { seconds: activeRefreshCount });
              }
              return t('refresh-stickers.button');
            })()}
          </Button>
        </div>
      </div>
    </SheetContent>
  );
}
